import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Pager, PRIORITY_LABEL, SALES_ACTIVITY_TYPES, TASK_OUTCOME_LABEL, Task, TaskOutcomeEnum, TaskPriorityEnum, TasksService, TaskStatusEnum, TaskTypeEnum, User, UserService, taskTypeLabelKey } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, finalize, map, Subject, takeUntil } from 'rxjs';

export const TASK_STATUS_LABEL: Record<TaskStatusEnum, string> = {
  [TaskStatusEnum.PENDING]: 'task.enum.statusPending',
  [TaskStatusEnum.IN_PROGRESS]: 'task.enum.statusInProgress',
  [TaskStatusEnum.DONE]: 'task.enum.statusDone',
};

/**
 * The tabs of the activity workspace (SPEC-2026-10-crm-activities-cadence RF-35, plan AD-11):
 * Hoje is the default; Prazo and Atrasadas were retired — Hoje, Semana and Mês cover their
 * buckets, overdue is Hoje's first group, and every range view shows the overdue count.
 */
export type ViewTab = 'today' | 'week' | 'month' | 'calendar' | 'dashboard' | 'task-list';
export const VALID_TABS = new Set<ViewTab>(['today', 'week', 'month', 'calendar', 'dashboard', 'task-list']);

/** The filters every activity view reads (RF-39, RF-44). Values travel as the API takes them. */
export type ActivityFilters = Record<string, string>;

@Component({
    selector: 'app-task',
    templateUrl: './task.component.html',
    styleUrl: './task.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskComponent extends AbstractComponent implements OnInit {
  private termOfSearch: Subject<string> = new Subject<string>();

  listUser: Array<User> = new Array<User>();

  /** The Lista tab's pager — the one listing that stays as it was. */
  pager: Pager<Task> = new Pager<Task>({ perPage: 10 });

  selectedUserId?: string;
  selectedOwnerId?: string;
  selectedStatus: TaskStatusEnum[] = [];
  selectedPriority: TaskPriorityEnum[] = [];
  selectedType?: string;
  selectedOutcome?: TaskOutcomeEnum;

  currentUserIdFromHeader: string;

  activeTab: ViewTab = 'today';

  /**
   * What the views read. Rebuilt as a NEW object on every change, so each view's `@Input()`
   * setter fires and reloads; the same filter block serves every tab (RF-39, plan AD-11).
   */
  filters: ActivityFilters = {};

  labelUser: string = 'task.userFilter';
  labelOwner: string = 'task.ownerFilter';
  statusLabel = 'task.statusFilter';
  priorityLabel = 'task.priorityFilter';
  typeLabel = 'task.home.typeFilter';
  outcomeLabel = 'task.home.outcomeFilter';

  status = Object.values(TaskStatusEnum).map(v => ({ value: v, label: TASK_STATUS_LABEL[v] }));
  priority = Object.values(TaskPriorityEnum).map(v => ({ value: v, label: PRIORITY_LABEL[v] }));
  /** The seven sales types (RF-01), labelled by the one shared helper. */
  types = SALES_ACTIVITY_TYPES.map((v: TaskTypeEnum) => ({ value: `${v}`, label: taskTypeLabelKey(`${v}`) }));
  outcomes = Object.values(TaskOutcomeEnum).map(v => ({ value: v, label: TASK_OUTCOME_LABEL[v] }));

  constructor(injector: Injector, private taskService: TasksService, private userService: UserService) {
    super(injector);
    this.currentUserIdFromHeader = this.route.snapshot.queryParams['userId'];
  }

  /**
   * Name of the opportunity a task belongs to
   * (SPEC-2026-08-crm-pipeline-deals-radar).
   *
   * The listing joins the deal as `{ _id, name, removed }` (trendmulti-api task.service
   * buildLookupPipeline) and `getById` populates it in full, so `Task.card` is typed
   * as the deal — the same partial-populate compromise `Task.user` already makes.
   */
  dealName(task: Task): string {
    return task?.card?.name ?? '';
  }

  /** The task's deal was deleted since (E4 RF-52): the task is then one with no deal. */
  isDealRemoved(task: Task): boolean {
    return task?.card?.removed === true;
  }

  ngOnInit(): void {
    const requested = this.route.snapshot.queryParams['tab'] as ViewTab;
    if (requested && VALID_TABS.has(requested)) this.activeTab = requested;

    this.getListUser();
    this.setupObservableSearch();
    this.rebuildFilters();
  }

  /** The responsible-person filter is offered to users permitted to see all deals — the rule the API applies (RF-44). */
  get canChooseResponsible(): boolean {
    return this.canViewAllDeals();
  }

  get showSearch(): boolean {
    return this.activeTab === 'task-list' || this.activeTab === 'week' || this.activeTab === 'month';
  }

  onTabClick(tab: ViewTab) {
    if (!VALID_TABS.has(tab) || tab === this.activeTab) return;
    this.activeTab = tab;
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, queryParamsHandling: 'merge', replaceUrl: true });
    if (tab === 'task-list') {
      this.pager.page = 1;
      this.loadList();
    }
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.loadList();
  }

  private getListUser(): void {
    this.userService.getList().subscribe({
      next: (list: Array<User>) => {
        this.listUser = list ?? [];
        // The header shortcut lands here with the current user: preselect them as the responsible.
        const fromHeader = this.listUser.find(u => u._id === this.currentUserIdFromHeader);
        if (fromHeader) this.changeUser(fromHeader);
      },
      error: (err) => this.alertService.error(this.t('task.home.errorLoadingUsers'), err),
    });
  }

  private setupObservableSearch(): void {
    this.termOfSearch.pipe(
      takeUntil(this.destroy$),
      map(v => (v ?? '').trim()),
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe({
      next: (term) => {
        this.searchString = term;
        this.pager.page = 1;
        this.rebuildFilters();
      },
      error: (err) => this.alertService.error(this.t('task.home.errorSearching'), err),
    });
  }

  search(term: string) {
    this.termOfSearch.next(term);
  }

  goToTaskDetail(id: string): void {
    this.router.navigate([`/task/${id}`]);
  }

  translateEnuns(value: string) {
    return PRIORITY_LABEL[value as TaskPriorityEnum] || TASK_STATUS_LABEL[value as TaskStatusEnum] || value;
  }

  getImage(idUser: unknown): string {
    if (idUser) {
      return this.listUser.filter((user: User) => user._id === idUser)
        .map((user: User) => user.image)
        .reduce((image: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  getOwnerUserName(idUser: unknown): string {
    if (idUser) {
      return this.listUser.filter((user: User) => user._id === idUser)
        .map((user: User) => user.name)
        .reduce((name: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  /** One filter object for every view; the Lista reloads at once, the others through their `@Input()`. */
  private rebuildFilters(): void {
    const filters: ActivityFilters = {};
    if (this.selectedOwnerId) filters['owner'] = this.selectedOwnerId;
    if (this.selectedUserId) filters['user'] = this.selectedUserId;
    if (this.selectedStatus?.length) filters['status'] = this.selectedStatus.join(',');
    if (this.selectedPriority?.length) filters['priority'] = this.selectedPriority.join(',');
    if (this.selectedType) filters['type'] = this.selectedType;
    if (this.selectedOutcome) filters['outcome'] = this.selectedOutcome;
    if (this.searchString) filters['search'] = this.searchString;
    this.filters = filters;

    if (this.activeTab === 'task-list') this.loadList();
  }

  /** The Lista tab: `ordered-list` without the retired `order` parameter (plan AD-11). */
  private loadList() {
    const { search: _search, ...params } = this.filters;

    this.loadingContent = true;
    this.taskService.getTasksByDate(this.pager.page, this.pager.perPage, this.searchString, params)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
      .subscribe({
        next: (res: Pager<Task>) => this.pager = res,
        error: (e) => this.alertService.error(this.t('task.home.errorLoadingTasks'), e),
      });
  }

  changeOwner(user: User): void {
    this.selectedOwnerId = user?._id ?? undefined;
    this.labelOwner = user ? user.name : 'task.ownerFilter';
    this.pager.page = 1;
    this.rebuildFilters();
  }

  changeUser(user: User): void {
    this.selectedUserId = user?._id ?? undefined;
    this.labelUser = user ? user.name : 'task.userFilter';
    this.pager.page = 1;
    this.rebuildFilters();
  }

  changeStatus(status: TaskStatusEnum): void {
    this.statusLabel = status ? TASK_STATUS_LABEL[status] : 'task.statusFilter';
    this.selectedStatus = status ? [status] : [];
    this.pager.page = 1;
    this.rebuildFilters();
  }

  changePriority(priority: TaskPriorityEnum): void {
    this.priorityLabel = priority ? PRIORITY_LABEL[priority] : 'task.priorityFilter';
    this.selectedPriority = priority ? [priority] : [];
    this.pager.page = 1;
    this.rebuildFilters();
  }

  /** E4 RF-39: activity type and outcome. */
  changeType(type: string | null): void {
    this.selectedType = type || undefined;
    this.typeLabel = type ? taskTypeLabelKey(type) : 'task.home.typeFilter';
    this.pager.page = 1;
    this.rebuildFilters();
  }

  changeOutcome(outcome: TaskOutcomeEnum | null): void {
    this.selectedOutcome = outcome || undefined;
    this.outcomeLabel = outcome ? TASK_OUTCOME_LABEL[outcome] : 'task.home.outcomeFilter';
    this.pager.page = 1;
    this.rebuildFilters();
  }
}
